import React, { useRef, useState } from 'react';
import {
  Box,
  Button as MuiButton,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { ImageBlockData } from '../../../../models/courses';

interface ImageBlockProps {
  data: ImageBlockData;
  onChange: (_data: ImageBlockData) => void;
  onUpload: (_file: File) => Promise<string>;
  disabled?: boolean;
}

export const ImageBlock: React.FC<ImageBlockProps> = ({
  data,
  onChange,
  onUpload,
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    setUploading(true);
    try {
      const url = await onUpload(file);
      onChange({ ...data, src: url });
    } finally {
      setUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          style={{ display: 'none' }}
          onChange={handleFile}
          disabled={disabled || uploading}
        />
        <MuiButton
          variant="outlined"
          size="small"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
          startIcon={uploading ? <CircularProgress size={14} /> : undefined}
        >
          {uploading ? 'Загрузка...' : 'Загрузить изображение'}
        </MuiButton>
        {data.src && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ wordBreak: 'break-all', maxWidth: 300 }}
          >
            {data.src}
          </Typography>
        )}
      </Box>
      {data.src && (
        <Box
          component="img"
          src={data.src}
          alt={data.alt}
          sx={{
            maxWidth: 300,
            maxHeight: 200,
            objectFit: 'contain',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        />
      )}
      <TextField
        label="Alt-текст"
        size="small"
        fullWidth
        value={data.alt}
        onChange={(e) => onChange({ ...data, alt: e.target.value })}
        disabled={disabled}
      />
      <TextField
        label="Подпись"
        size="small"
        fullWidth
        value={data.caption}
        onChange={(e) => onChange({ ...data, caption: e.target.value })}
        disabled={disabled}
      />
    </Box>
  );
};
